"use client";
import AppEmpty from "@/components/ui/app-empty";
import InfiniteScroll from "@/components/ui/infinite-scroll";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { searchProduct } from "@/services/search.service";
import { CategoryModel } from "@/types/category";
import { ProductModel } from "@/types/product";
import { PopularSearchModel } from "@/types/search";
import { Loader2, PackageSearch, Search, X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useRef, useState } from "react";
interface Props {
  popularCategories: CategoryModel[];
  popularSearch: PopularSearchModel[];
  customerSearch: PopularSearchModel[] | null;
  setOpen: (open: boolean) => void;
}

const SearchContent = ({
  popularCategories,
  popularSearch,
  customerSearch,
  setOpen,
}: Props) => {
  const t = useTranslations();

  const [q, setQ] = useState("");
  const [results, setResults] = useState<ProductModel[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const locale = useLocale();

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const hasMore = page < lastPage;

  const handleSearch = (value: string) => {
    setQ(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);

    debounceRef.current = setTimeout(async () => {
      if (!value.trim()) {
        setResults([]);
        setPage(1);
        setLastPage(1);
        setLoading(false);
        return;
      }

      const data = await searchProduct(value, 1);
      setResults(data.results);
      setPage(data.current_page);
      setLastPage(data.last_page);
      setLoading(false);
    }, 500); // 500ms debounce
  };

  const loadMore = async () => {
    setIsLoading(true);
    const nextPage = page + 1;
    const data = await searchProduct(q, nextPage);
    setResults([...results, ...data.results]);
    setPage(nextPage);
    setLastPage(data.last_page);
    setIsLoading(false);
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  return (
    <>
      <div className="flex items-center gap-2 mb-4 relative">
        <Search className="size-5 text-gray-500" />

        <Input
          autoFocus
          placeholder={t("startTyping")}
          className={cn("flex-1", locale === "ar" ? "pl-8" : "pr-8")}
          value={q}
          onChange={(e) => handleSearch(e.target.value)}
        />

        {q && (
          <button
            type="button"
            onClick={() => handleSearch("")}
            className={cn(
              "absolute text-gray-400 hover:text-gray-600",
              locale === "ar" ? "left-2" : "right-2",
            )}
          >
            <X className="size-4" />
          </button>
        )}
      </div>
      {!q && !loading && (
        <div className="space-y-6">
          {/* Popular Categories */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">
              {t("popularCategories")}
            </h3>
            <div className="flex flex-wrap gap-2">
              {popularCategories.map((c) => (
                <Link
                  key={c.id}
                  href={`/categories/${c.id}`}
                  className="px-3 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-sm"
                  onClick={() => setOpen(false)}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Popular Searches */}
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">
              {t("popularSearch")}
            </h3>
            <ul className="flex flex-wrap gap-2">
              {popularSearch.map((item) => (
                <li key={item.id}>
                  <button
                    className="border py-1 px-2 rounded-full text-left text-sm text-gray-600 hover:text-black"
                    onClick={() => handleSearch(item.search_query)}
                  >
                    {item.search_query}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Customer Previous Searches */}
          {customerSearch && customerSearch?.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-700 mb-2">
                {t("youRecentSearch")}
              </h3>
              <ul className="flex flex-wrap gap-2">
                {customerSearch?.map((item) => (
                  <li key={item.id}>
                    <button
                      className="border py-1 px-2 rounded-full text-left text-sm text-gray-600 hover:text-black"
                      onClick={() => handleSearch(item.search_query)}
                    >
                      {item.search_query}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* SKELETON LOADING */}
      {loading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4 animate-pulse">
              <div className="w-[60px] h-[80px] bg-gray-200 rounded-md"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 w-1/2 bg-gray-200 rounded"></div>
                <div className="h-4 w-1/4 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {!loading && results.length === 0 && q && (
        <AppEmpty
          title={t("noProducts")}
          description={t("noProductsMatchYourSearch")}
          icon={<PackageSearch />}
        />
      )}

      {/* Results */}
      {!loading && results.length > 0 && (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {results.map((product, index) => (
            <React.Fragment key={product.id}>
              <Link
                href={`/products/${product.slug}`}
                className="flex gap-4 items-center rounded-lg p-2 hover:bg-gray-50"
                onClick={() => setOpen(false)}
              >
                <Image
                  width={60}
                  height={100}
                  src={product.thumbnail_url}
                  alt={product.name}
                  className="rounded-lg"
                />

                <div className="space-y-1">
                  <h2>{product.name}</h2>
                  <h1 className="font-semibold text-xl">{product.price} SYP</h1>
                </div>
              </Link>

              {index < results.length - 1 && <Separator />}
            </React.Fragment>
          ))}
          <InfiniteScroll
            isLoading={isLoading}
            hasMore={hasMore}
            next={loadMore}
            threshold={1}
          >
            {hasMore && (
              <Loader2 className="my-4 mx-auto h-8 w-8 animate-spin" />
            )}
          </InfiniteScroll>
        </div>
      )}
    </>
  );
};

export default SearchContent;
